/**
 * Per-image render size, held on the image's **ref** (DEV-697 Phase 2).
 *
 * An image project's render size comes from `project.json > images[]` and
 * nowhere else: the player does `project.dimensions = imageRef.dimensions`
 * (`prepareImageProject()`), and `ImageModel` has no dimension field at all.
 * The three setters that used to live in `image-form` patched `image.json`
 * instead, so they changed nothing the player ever read — they are gone.
 *
 * Phase 1 made the backend stamp every ref that lacks a size, derived from
 * `dimensionsMap` (`.medium ?? first`) of the chapter or project. That gives
 * every image in a project the *same* size. This module is the override: a
 * single image that needs its own aspect ratio — a 16:9 still inside a 9:16
 * project — without a second project.
 *
 * ## Width picks the file; height only shapes the canvas
 *
 * `dimensions.width` is fed into `generateAbsFilePath()` to choose the
 * **resolution-suffixed file**, so it has to match a width that actually has a
 * generated version — an invented width is a 404, not a resize. `height` never
 * selects anything; it only sets the canvas. That asymmetry is why
 * `{ width: 720, height: 405 }` expresses 16:9 inside a 9:16 project with no
 * new media: the 720 file already exists, the canvas is simply shorter.
 *
 * ## The derivation rule is NOT duplicated here
 *
 * Phase 1 deliberately put the fallback in the backend only, so a frontend copy
 * cannot drift. Clearing an override therefore does not recompute anything
 * locally: the fields go empty and `fillImageRefDimensions()` re-derives on the
 * next save, because it fills only what is missing and never overwrites.
 */

export interface Dimensions {
	width: number;
	height: number;
}

/** The subset of `ImageRef` this module reads. */
export interface ImageRefDimensions {
	dimensions?: Dimensions | null;
	origCoordsWidth?: number | null;
}

/**
 * What the ref holds, as written to `project.json`.
 */
export interface DimensionsPatch {
	dimensions: Dimensions | null;
	origCoordsWidth: number | null;
}

/**
 * What the form holds while it is being typed.
 *
 * Each axis is independently nullable, which the ref itself cannot express —
 * and that is the point. `p-inputnumber` emits `null` for a momentarily empty
 * field, so overwriting a height sends `null` before it sends the new number.
 * An earlier version treated that `null` as "drop the whole override" and
 * wiped the width with it; the next keystrokes then rebuilt the height on a
 * width of 0, which never became persistable, so nothing saved and the width
 * read 0 on screen. Axes now clear one at a time.
 */
export interface DimensionsDraft {
	width: number | null;
	height: number | null;
	origCoordsWidth: number | null;
}

function finite(value: unknown): number | null {
	return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Read a ref into an editable draft. */
export function readDimensionsDraft(ref: ImageRefDimensions | null | undefined): DimensionsDraft {
	return {
		width: finite(ref?.dimensions?.width),
		height: finite(ref?.dimensions?.height),
		origCoordsWidth: finite(ref?.origCoordsWidth),
	};
}

/** Set (or clear, with `null`) one axis. The other is untouched. */
export function withDimension(
	draft: DimensionsDraft,
	field: 'width' | 'height',
	value: number | null,
): DimensionsDraft {
	return { ...draft, [field]: value };
}

export function withOrigCoordsWidth(draft: DimensionsDraft, value: number | null): DimensionsDraft {
	return { ...draft, origCoordsWidth: value };
}

/** Drop the override entirely; the backend re-derives it on the next save. */
export function clearedDimensions(): DimensionsDraft {
	return { width: null, height: null, origCoordsWidth: null };
}

export function hasOverride(draft: DimensionsDraft): boolean {
	return draft.width !== null || draft.height !== null || draft.origCoordsWidth !== null;
}

/**
 * Whether this draft may be written to the ref.
 *
 * Both axes absent is fine — that is a cleared override, and the backend
 * re-derives. Both present and real is fine. Anything in between is a state
 * mid-edit: a half-filled size must NOT reach `project.json`, because the
 * backend's `usableDimensions()` would keep a 0 as a deliberate value, pin the
 * ref forever and leave the player asking for a file 0 px wide.
 */
export function isPersistable(draft: DimensionsDraft): boolean {
	if (draft.width === null && draft.height === null) return true;
	return (draft.width ?? 0) > 0 && (draft.height ?? 0) > 0;
}

/** The ref-shaped value this draft writes. Only call it when {@link isPersistable}. */
export function toRefPatch(draft: DimensionsDraft): DimensionsPatch {
	const complete = draft.width !== null && draft.height !== null;
	return {
		dimensions: complete ? { width: draft.width as number, height: draft.height as number } : null,
		origCoordsWidth: draft.origCoordsWidth,
	};
}

/**
 * Why this size is unusable, or `null`.
 *
 * Describes a ref the player chokes on rather than a matter of taste: one axis
 * without the other, or a zero. It is the message for a state
 * {@link isPersistable} refuses to write, and it also fires for a broken size
 * that reached `project.json` some other way (Monaco, an import), where nothing
 * stopped it and saying so is all the form can do.
 */
export function dimensionsWarning(draft: DimensionsDraft): string | null {
	if (draft.width === null && draft.height === null) return null;
	if (draft.width === null || draft.height === null || draft.width <= 0 || draft.height <= 0) {
		return 'Both width and height are required — the player reads width to pick the file and height to size the canvas.';
	}
	return null;
}

/**
 * Whether `width` matches one of the widths the project actually generates.
 *
 * Advisory: `availableWidths` comes from the project's `dimensionsMap`, and a
 * project can legitimately declare a width on a chapter the caller did not
 * collect. An empty list means "nothing to compare against" and never warns.
 */
export function unknownWidthWarning(
	draft: DimensionsDraft,
	availableWidths: readonly number[],
): string | null {
	const width = draft.width;
	if (!width || width <= 0 || availableWidths.length === 0) return null;
	if (availableWidths.includes(width)) return null;
	return `No generated version is ${width} px wide (project has ${availableWidths.join(', ')}). The player would request a file that does not exist.`;
}
